// Similar to the LookingFor app, but instead of asking for the user's interested in gentation
// (ie: straight men, bi men... )
// this wiw app takes in the genders that the user is interested in (ie: men, women)
// this app also features one field per page
// it's currently used in onboarding and will be used for unified settings in the future
// melissa, July 2017

import get from "lodash/get";

import _str from "shared/util/_str";
import GetGendersFromWiwSettings from "shared/util/GetGendersFromWiwSettings";
import WiwAgeRange from "shared/util/WiwAgeRange";

import {
	isFieldValid,
	getFeedback,
	calculateDistance,
	isNonMonogamyReboarding,
} from "apps/what_i_want/util/helpers";
import {
	fetchAPIFields,
	saveData,
	saveProfileDetails,
} from "apps/what_i_want/util/web";
import {
	FIELDS,
	FIELD_ORDER,
	PRONOUNS_MAP,
	BINARY_GENDERS,
	FIELDS_VALUE_MAP,
	FIELD_NAVIGATION,
	MONOGAMY_API_VALUES,
	RELATIONSHIP_STATUS_OPTIONS,
	NON_MONOGAMY_FIELD_ORDER,
	NON_MONOGAMY_REBOARDING_FIELD_ORDER,
	MONOGAMY_OPTIONS,
	DISTANCE_DIRECT_LINK_QUERY,
} from "apps/what_i_want/util/constants";
import { GENTATION } from "shared/dictionaries/gentation";
import { updateSentence } from "ducks/edit_wiw";

let hasLoadedThisSession = false;


// ////////////////////////
// ACTION TYPES //////////
// //////////////////////

export const FETCH_FIELDS = "WHAT_I_WANT.FETCH_FIELDS";
export const FETCH_FIELDS_SUCCESS = "WHAT_I_WANT.FETCH_FIELDS_SUCCESS";
export const FETCH_FIELDS_ERROR = "WHAT_I_WANT.FETCH_FIELDS_ERROR";
export const COMPONENT_MOUNTED = "WHAT_I_WANT.COMPONENT_MOUNTED";

export const SET_UP_FIELDS = "WHAT_I_WANT.SET_UP_FIELDS";
export const SET_FIELD_ORDER = "WHAT_I_WANT.SET_FIELD_ORDER";
export const SET_CURRENT_INDEX = "WHAT_I_WANT.SET_CURRENT_INDEX";
export const SET_INDEX_TO = "WHAT_I_WANT.SET_INDEX_TO";
export const UPDATE_FIELD_DATA = "WHAT_I_WANT.UPDATE_FIELD_DATA";

export const SAVE_FIELD_DATA = "WHAT_I_WANT.SAVE_FIELD_DATA";
export const SAVE_FIELD_DATA_SUCCESS = "WHAT_I_WANT.SAVE_FIELD_DATA_SUCCESS";
export const SAVE_FIELD_DATA_ERROR = "WHAT_I_WANT.SAVE_FIELD_DATA_ERROR";

export const CLEAR_FEEDBACK = "WHAT_I_WANT.CLEAR_FEEDBACK";
export const SHOW_FIELD = "WHAT_I_WANT.SHOW_FIELD";
export const CLEAR_CURRENT_FIELD = "WHAT_I_WANT.CLEAR_CURRENT_FIELD";


// ////////////////////////
// INITIAL STATE /////////
// //////////////////////

export const INITIAL_STATE = {
	isFetchingFields: false,
	isSettingUpFields: false,
	fetchFieldError: null,

	// from api call
	apiData: null,
	userApiData: null,
	genderOptions: null,
	relationshipStatus: null,
	monogamy: null,

	fieldValues: {},
	fieldOrder: [],
	currentField: null,
	currentFieldIndex: 0,
	desiredPronoun: null,
	hasSelectedAllGenders: false,
	isSaving: false,
	isLastField: false,
	errors: {},

	feedback: null,
};

// ////////////////////////
// REDUCER ///////////////
// //////////////////////

export function reducer(state = INITIAL_STATE, action) {
	switch (action.type) {
		case FETCH_FIELDS: {
			return {
				...state,
				isFetchingFields: true,
			};
		}

		case FETCH_FIELDS_SUCCESS: {
			// set up fields
			const {
				apiData,
				userApiData,
				genderOptions,
				genderTags,
				orientationFilter,
				relationshipStatus,
				monogamy,
				linkedAccount,
			} = action;

			return {
				...state,
				apiData,
				genderTags,
				userApiData,
				genderOptions,
				orientationFilter,
				relationshipStatus,
				monogamy,
				linkedAccount,
				isFetchingFields: false,
				isSettingUpFields: true,
			};
		}

		case FETCH_FIELDS_ERROR: {
			return {
				...state,
				fetchFieldError: action.error,
				isFetchingFields: false,
			};
		}

		case SET_UP_FIELDS: {
			let { fieldValues, errors, hasSelectedAllGenders } = state;
			const {
				userApiData,
				genderTags,
				genderOptions,
				orientationFilter,
				monogamy,
				relationshipStatus,
				linkedAccount,
			} = state;
			const fieldLength = action.fieldOrder.length;
			const availableGendersLength = genderOptions.availableTags.gender.length;

			// optionally, we can start with default values, instead of showing what
			// the user has selected (or, in the case of onboarding, the defaults that
			// the backend chose)
			if (action.shouldClearFields || !userApiData) {
				fieldValues = {
					ageMin: "",
					ageMax: "",
					distance: "",
					isMetric: false,
					lookingfor: [],
					genders: [],
					monogamy: MONOGAMY_OPTIONS.monogamous,
					relationshipStatus: RELATIONSHIP_STATUS_OPTIONS.single,

					monogamous: true, // default to true
					orientation: userApiData.user_orientation,
					userGender: userApiData.user_gender,
					isStraightOnly:
						userApiData.user_orientation === GENTATION.ORIENTATION_ALIASES.straight &&
						orientationFilter.straight_users === 1 && orientationFilter.bi_users === 0,
				};
			} else {
				fieldValues = {
					ageMin: userApiData.age_min,
					ageMax: userApiData.age_max,
					distance: calculateDistance(userApiData.distance, userApiData.metric),
					isMetric: userApiData.metric,
					lookingfor: [...userApiData.lookingfor],

					// genders is not really a wiw parameter
					// it's used to calculate gentation prior to hitting the wiw endpoint
					genders: [...genderTags],

					monogamy,
					relationshipStatus,
					linkedAccount,

					monogamous: userApiData.monogamous !== MONOGAMY_API_VALUES.nonMonogamous,
					orientation: userApiData.user_orientation,
					userGender: userApiData.user_gender,
					isStraightOnly:
						userApiData.user_orientation === GENTATION.ORIENTATION_ALIASES.straight &&
						orientationFilter.straight_users === 1 && orientationFilter.bi_users === 0,
				};
			}

			hasSelectedAllGenders = fieldValues.genders.length === availableGendersLength;

			// set up error state
			action.fieldOrder.forEach((field) => {
				if (isFieldValid(field, fieldValues)) {
					errors = {
						...errors,
						[field]: false,
					};
				} else {
					errors = {
						...errors,
						[field]: true,
					};
				}
			});

			let currentField;

			if (action.shouldClearCurrentField) {
				currentField = "";
			} else if (action.showLastStep) {
				currentField = action.fieldOrder[fieldLength - 1];
			} else {
				currentField = action.fieldOrder[0];
			}

			// allow direct linking to distance for CRM team
			if (window.location.search.includes(DISTANCE_DIRECT_LINK_QUERY)) {
				currentField = FIELDS.DISTANCE;
			}

			return {
				...state,
				errors,
				fieldOrder: action.fieldOrder,
				currentFieldIndex: action.showLastStep ? fieldLength - 1 : 0,
				currentField,
				fieldValues,
				hasSelectedAllGenders,
				isSettingUpFields: false,
			};
		}

		case SET_FIELD_ORDER: {
			const { fieldOrder } = action.payload;

			if (!fieldOrder || !Array.isArray(fieldOrder)) {
				return { ...state };
			}

			return {
				...state,
				fieldOrder,
				isLastField: state.currentFieldIndex === fieldOrder.length - 1,
			};
		}

		case SET_CURRENT_INDEX: {
			const { direction } = action;
			const { fieldOrder } = state;

			let currentFieldIndex = state.currentFieldIndex;
			let isLastField = false;

			if (direction === FIELD_NAVIGATION.NEXT && currentFieldIndex < state.fieldOrder.length - 1) {
				currentFieldIndex += 1;
			} else if (direction === FIELD_NAVIGATION.PREVIOUS && currentFieldIndex > 0) {
				currentFieldIndex -= 1;
			}

			if (currentFieldIndex === fieldOrder.length - 1) {
				isLastField = true;
			}

			return {
				...state,
				isLastField,
				currentField: fieldOrder[currentFieldIndex],
				currentFieldIndex,
				feedback: null,
			};
		}

		case SET_INDEX_TO: {
			const { index } = action.payload;
			const { fieldOrder } = state;

			if (typeof index !== "number" || index < 0 || index > (fieldOrder.length - 1)) {
				return state;
			}

			return {
				...state,
				currentFieldIndex: index,
				currentField: state.fieldOrder[index],
			};
		}

		case UPDATE_FIELD_DATA: {
			let {
				fieldValues, desiredPronoun, errors, feedback, hasSelectedAllGenders,
			} = state;
			const { currentField, apiData, genderOptions } = state;
			const { fieldName } = action;
			let fieldData = action.fieldData;
			let previousInput = fieldValues[fieldName];
			let input = fieldData;

			// send age min and age max together
			if (currentField === FIELDS.AGE) {
				// make sure age min is smaller than age max
				if (fieldName === "ageMin" && fieldValues.ageMax && fieldData > fieldValues.ageMax) {
					const ageMax = fieldValues.ageMax;
					fieldValues.ageMax = fieldData;
					fieldData = ageMax;
				} else if (fieldName === "ageMax" && fieldValues.ageMin && fieldData < fieldValues.ageMin) {
					const ageMin = fieldValues.ageMin;
					fieldValues.ageMin = fieldData;
					fieldData = ageMin;
				}

				previousInput = {
					ageMin: fieldValues.ageMin,
					ageMax: fieldValues.ageMax,
				};

				input = {
					...previousInput,
					[fieldName]: fieldData,
				};
			}

			fieldValues = {
				...fieldValues,
				[fieldName]: fieldData,
			};

			const isCurrentFieldValid = isFieldValid(currentField, fieldValues);

			errors = {
				...errors,
				[currentField]: !isCurrentFieldValid,
			};

			// if they chose a desired gender, use store the appropriate pronoun
			if (fieldName === FIELDS_VALUE_MAP[FIELDS.GENDER]) {
				if (fieldData.length === 1 && fieldData[0] === BINARY_GENDERS.man) {
					desiredPronoun = PRONOUNS_MAP.he;
				} else if (fieldData.length === 1 && fieldData[0] === BINARY_GENDERS.woman) {
					desiredPronoun = PRONOUNS_MAP.she;
				} else {
					desiredPronoun = PRONOUNS_MAP.they;
				}
			}

			if (fieldName === FIELDS_VALUE_MAP[FIELDS.GENDER]) {
				if (fieldData.length === genderOptions.availableTags.gender.length) {
					hasSelectedAllGenders = true;
				} else {
					hasSelectedAllGenders = false;
				}
			}

			feedback = getFeedback(currentField, input, previousInput, apiData[fieldName]);

			return {
				...state,
				errors,
				fieldValues,
				desiredPronoun,
				hasSelectedAllGenders,
				feedback,
			};
		}

		case CLEAR_FEEDBACK: {
			return {
				...state,
				feedback: null,
			};
		}

		case COMPONENT_MOUNTED: {
			return {
				...state,
				entryPoint: action.entryPoint,
			};
		}

		case SAVE_FIELD_DATA: {
			return {
				...state,
				isSaving: true,
				errors: {
					...state.errors,
					general: false,
				},
			};
		}

		case SAVE_FIELD_DATA_SUCCESS: {
			return {
				...state,
				isSaving: false,
				errors: {
					...state.errors,
					general: false,
				},
			};
		}

		case SAVE_FIELD_DATA_ERROR: {
			return {
				...state,
				errors: {
					...state.errors,
					general: action.error,
				},
				isSaving: false,
			};
		}

		case SHOW_FIELD: {
			return {
				...state,
				currentField: action.fieldName,
			};
		}

		case CLEAR_CURRENT_FIELD: {
			return {
				...state,
				currentField: "",
			};
		}

		default:
			return state;
	}
}


// ////////////////////////
// ACTION CREATORS ///////
// //////////////////////

export function fetchAndSetupFields({
	shouldClearFields = false,
	fieldOrder = FIELD_ORDER,
	shouldClearCurrentField = false,
	showLastStep = false,
	isNonMonogamyOnboarding = false,
} = {}) {
	return (dispatch, getStore) => {
		dispatch({
			type: FETCH_FIELDS,
		});

		const profileDetails = getStore().profileDetails;
		const isOnboarding = !!getStore().onboarding;
		let birthday;

		if (
			profileDetails &&
			profileDetails.fields &&
			profileDetails.fields.birthdate &&
			profileDetails.fields.birthdate.data
		) {
			birthday = profileDetails.fields.birthdate.data;
		}

		fetchAPIFields(isNonMonogamyOnboarding)
			.then((res) => {
				const wiwRes = res[0];
				const orientationFilterRes = res[1];

				if (
					!(wiwRes.wiw && wiwRes.wiw.parameters && wiwRes.wiw.values) ||
					!wiwRes.gender_options ||
					!orientationFilterRes
				) {
					dispatch({
						type: FETCH_FIELDS_ERROR,
						error: _str("Oops, something went wrong. Please try again!"),
					});

					return;
				}

				const wiwValues = wiwRes.wiw.values;

				// prepare a default age range if we have a birthday and a null/default
				// WIW min- and max-age (or if we're in onboarding and have 18-99 set)
				if (
					birthday &&
					(
						(!wiwValues.age_min && !wiwValues.age_max) ||
						(isOnboarding && wiwValues.age_min === 18 && wiwValues.age_max === 99)
					)
				) {
					const ageRange = WiwAgeRange.getRangeFromBirthday(birthday);

					wiwValues.age_min = ageRange.minAge;
					wiwValues.age_max = ageRange.maxAge;
				}

				// in the new non-monogamy world, non monogamous relationships aren't a
				// relationship status, they are a user attribute. so filter that shit out!
				wiwValues.lookingfor = wiwValues.lookingfor
					.filter((r) => r !== "non_monogamous_relationships");

				let genderTags;

				// we never want a default for desired gender; if the user has come
				// through new signup, we just don't know their orientation or anything
				// like that.
				if (
					isOnboarding &&
					(getStore().onboarding.configName === "ONBOARDING_FUNNEL" ||
						getStore().onboarding.configName === "ONBOARDING_FUNNEL_WITH_PROFILE_DETAILS") &&
					!hasLoadedThisSession
				) {
					wiwValues.gender = null;
					genderTags = [];
				} else {
					genderTags = GetGendersFromWiwSettings(wiwValues);
				}

				const hasLinkedAccount = wiwRes.linked_account && wiwRes.linked_account.reciprocal;
				const showNonMonogamyFlow = (
					wiwRes.monogamous !== 1 // not monogamous
					|| wiwRes.rel_status !== 1 // not single
					|| hasLinkedAccount // I refuse to comment this
				);

				// calculate the field order:
				// * if we have the non-monogamy update and aren't in reboarding, show
				//   the normal fields
				// * if we have the non-monogamy update, aren't in reboarding, and are
				//   non-monogamous, show the full normal+non-monogamy fields
				// * if we are in non-monogamy reboarding, show the minimal non-
				//   monogamy-only fields
				let _fieldOrder = fieldOrder;

				if (isNonMonogamyReboarding()) {
					_fieldOrder = NON_MONOGAMY_REBOARDING_FIELD_ORDER;
				} else if (showNonMonogamyFlow) {
					_fieldOrder = NON_MONOGAMY_FIELD_ORDER;
				}

				dispatch({
					type: FETCH_FIELDS_SUCCESS,
					apiData: wiwRes.wiw.parameters,
					userApiData: wiwValues,
					genderOptions: wiwRes.gender_options,
					genderTags,
					orientationFilter: orientationFilterRes,
					relationshipStatus: wiwRes.rel_status,
					monogamy: wiwRes.monogamous,
					linkedAccount: hasLinkedAccount ? wiwRes.linked_account : null,
				});

				dispatch({
					type: SET_UP_FIELDS,
					fieldOrder: _fieldOrder,
					showLastStep,
					shouldClearFields,
					shouldClearCurrentField,
				});

				hasLoadedThisSession = true;
			})
			.catch((err) => {
				dispatch({
					type: FETCH_FIELDS_ERROR,
					error: err,
				});

				console.warn("Fetch fields error:", err);
			});
	};
}

export function updateFieldData(fieldName, fieldData) {
	return {
		type: UPDATE_FIELD_DATA,
		fieldName,
		fieldData,
	};
}

export function showField(fieldName) {
	return {
		type: SHOW_FIELD,
		fieldName,
	};
}

export function mountedComponent(entryPoint) {
	return { type: COMPONENT_MOUNTED, entryPoint };
}

export function clearCurrentField() {
	return {
		type: CLEAR_CURRENT_FIELD,
	};
}

export function save(currentField, fieldValues) {
	return (dispatch, getStore) => {
		const store = getStore().whatIWant;

		dispatch({ type: SAVE_FIELD_DATA, errors: store.errors });

		// grab the store to get fieldValues
		const data = store.fieldValues;

		// get the config from the store to decide if the gender endpoint should
		// allow your orientation to be set based on your desired gender. it SHOULD
		// infer on the funnel, and SHOULDN'T anywhere else.
		data.allow_bi_inference = !!(store.configName && store.configName === "ONBOARDING_FUNNEL");

		// fuck it, for now the web file needs to know the calling context, so that
		// it knows whether or not to send the orientation filter calls.
		data.configName = store.configName;

		// the api call doesn't need to know which particular field we're saving
		// but the analytic events do
		saveData(data)
			.then((res) => {
				if (!res) {
					return dispatch({
						type: SAVE_FIELD_DATA_ERROR,
						error: _str("Oops, something went wrong."),
					});
				}

				const wiwSentence = get(res, "wiw.sentence.sentence");

				if (wiwSentence) {
					dispatch(updateSentence(res.wiw.sentence.sentence));
				}

				return dispatch({
					type: SAVE_FIELD_DATA_SUCCESS,
					payload: {
						currentField,
						fieldValues,
					},
					res,
				});
			})
			.catch((err) => {
				return dispatch({
					type: SAVE_FIELD_DATA_ERROR,
					error: err,
				});
			});
	};
}

export function setIndexTo(index) {
	return {
		type: SET_INDEX_TO,
		payload: { index },
	};
}

export function saveProfileDetail(field, data) {
	return (dispatch, getStore) => {
		const store = getStore().whatIWant;
		dispatch({ type: SAVE_FIELD_DATA, errors: store.errors });

		saveProfileDetails(data)
			.then((res) => {
				dispatch({
					type: SAVE_FIELD_DATA_SUCCESS,
					payload: {
						currentField: store.currentField,
						fieldValues: store.fieldValues,
					},
					res,
				});
			})
			.catch((err) => {
				const failedFieldIndex = store.fieldOrder.indexOf(field);

				dispatch({
					type: SAVE_FIELD_DATA_ERROR,
					error: err && err.error && err.error.message
						? `Error: ${err.error.message}`
						: err,
				});

				dispatch({
					type: SET_INDEX_TO,
					payload: {
						index: failedFieldIndex,
					},
				});
			});
	};
}

export function setCurrentIndex(direction) {
	return {
		type: SET_CURRENT_INDEX,
		direction,
	};
}

export function clearFeedback() {
	return {
		type: CLEAR_FEEDBACK,
	};
}

export function enableNonMonogamy() {
	return (dispatch) => {
		// add the non-monogamy fields to the list of active fields...
		dispatch({
			type: SET_FIELD_ORDER,
			payload: {
				fieldOrder: NON_MONOGAMY_FIELD_ORDER,
			},
		});

		// ...then go to the next field to get to non-monogamy
		dispatch(setCurrentIndex(FIELD_NAVIGATION.NEXT));
	};
}

export function setFieldOrder(fieldOrder) {
	return {
		type: SET_FIELD_ORDER,
		payload: {
			fieldOrder,
		},
	};
}



// WEBPACK FOOTER //
// ./src/ducks/what_i_want.js